import { ChangeEvent, useCallback, useState } from "react";

import { SearchCodeModalForm } from "src/components/common/form/search/SearchCodeModalForm";
import { SearchDateRangeForm } from "src/components/common/form/search/SearchDateRangeForm";
import { SearchDropDownForm } from "src/components/common/form/search/SearchDropDownForm";
import { SearchSectionForm } from "src/components/common/form/search/SearchSectionForm";

import { searchCustomer } from "src/apis/customerApi";
import { getItemPopupList } from "src/apis/itemApi";

import { InfoCodeSearchFieldConfig } from "src/types/info-form";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";

import useUiuxStore from "src/store/useUiuxStore";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { defaultWMS1060_R01Filter, useWMS1060_R01Store } from "./Store_WMS1060_R01";
import { SearchSectionDoubleForm } from "src/components/common/form/search/SearchSectionDoubleForm";

interface Props {
    onEnterKeyDown: () => void;
}

export const SearchSection_WMS1060_R01 = (props: Props) => {
    const { onEnterKeyDown } = props;
    
    // ========================================
    // Store
    // ========================================
    const { WMS1060_R01Filter, setWMS1060_R01Filter } = useWMS1060_R01Store();
    const { setCodeSearchModalProps } = useUiuxStore();
    const { 
        wmsCdList,          // 물류센터
        warehouseCdList,    // 창고
        inoutTypeList,      // 입출고분류
        inoutClassList,     // 수불유형
        itemGroupList       // 품목그룹
    } = useCommonCodeStore();
    
    // ========================================
    // State
    // ========================================
    const [pickerVisible, setPickerVisible] = useState(false);
    const [pickerPosition, setPickerPosition] = useState<{ top: number; left: number }>({ 
        top: 0, 
        left: 0 
    });
    
    // ========================================
    // Focus ID
    // ========================================
    const custInfoHandleFocusId = 'infoCustInput_WMS1060_R01';
    const itemInfoHandleFocusId = 'infoItemInput_WMS1060_R01';
    
    // ========================================
    // Info Form 코드 검색 설정
    // ========================================
    const infoFieldConfigs: InfoCodeSearchFieldConfig[] = [
        {
            fieldName: 'customCd',
            category: '거래처',
            focusId: custInfoHandleFocusId,
            responseMapping: {
                customCd: 'customCd',
                customNm: 'customNm',
            },
            searchFunction: async (searchText: string) => {
                const response = await searchCustomer(searchText);
                return response.data.data.find(v => v.customCd === searchText);
            }
        },
        {
            fieldName: 'itemCd',
            category: '품목',
            focusId: itemInfoHandleFocusId,
            responseMapping: {
                itemCd: 'itemCd',
                itemNm: 'itemNm',
            },
            searchFunction: async (searchText: string) => {
                const response = await getItemPopupList(searchText, '', '', '');
                return response.data.data.find((v: any) => v.itemCd === searchText);
            }
        },
    ];
    
    const { getFieldHandlers } = useInfoFormEvents({
        editData: WMS1060_R01Filter,
        setEditData: setWMS1060_R01Filter,
        defaultData: defaultWMS1060_R01Filter,
        infoFieldConfigs,
        setCodeSearchModalProps,
        formName: 'WMS1060_R01_search'
    });
    
    // ========================================
    // 입력 핸들러
    // ========================================
    const handleCustChange = (e: ChangeEvent<HTMLInputElement>) => {
        setWMS1060_R01Filter({ customCd: e.target.value });
    };
    
    const handleItemChange = (e: ChangeEvent<HTMLInputElement>) => {
        setWMS1060_R01Filter({ itemCd: e.target.value });
    };
    
    // ========================================
    // 더블클릭 핸들러
    // ========================================
    const handleCustDoubleClick = useCallback(async (e: React.MouseEvent<HTMLInputElement>) => {
        e.currentTarget.blur();
        getFieldHandlers('customCd').onDoubleClick(e);
    }, [getFieldHandlers]);
    
    const handleItemDoubleClick = useCallback(async (e: React.MouseEvent<HTMLInputElement>) => {
        e.currentTarget.blur();
        getFieldHandlers('itemCd').onDoubleClick(e);
    }, [getFieldHandlers]);
    
    // ========================================
    // UI 렌더링
    // ========================================
    return (
        <SearchSectionDoubleForm>
            {/* 1줄: 물류센터, 창고, 출고기간 */}
            <SearchDropDownForm
                label="물류센터"
                value={WMS1060_R01Filter.logicCenter}
                optionList={wmsCdList}
                onChangeValue={(value: string) => setWMS1060_R01Filter({ logicCenter: value })}
            />
            
            <SearchDropDownForm
                label="창고"
                value={WMS1060_R01Filter.warehouseCd}
                optionList={warehouseCdList}
                onChangeValue={(value: string) => setWMS1060_R01Filter({ warehouseCd: value })}
            />
            
            <SearchDateRangeForm
                label="출고기간"
                visible={pickerVisible}
                setVisible={setPickerVisible}
                dateRange={WMS1060_R01Filter.dateRange}
                setDateRange={(dateRange) => setWMS1060_R01Filter({ dateRange })}
                pickerPosition={pickerPosition}
                setPickerPosition={setPickerPosition}
            />
            
            {/* 2줄: 입출고분류, 수불유형, 품목그룹 */}
            <SearchDropDownForm
                label="입출고분류"
                value={WMS1060_R01Filter.inoutType}
                optionList={inoutTypeList}
                onChangeValue={(value: string) => setWMS1060_R01Filter({ inoutType: value })}
            />
            
            <SearchDropDownForm
                label="수불유형"
                value={WMS1060_R01Filter.inoutClass}
                optionList={inoutClassList}
                onChangeValue={(value: string) => setWMS1060_R01Filter({ inoutClass: value })}
            />
            
            <SearchDropDownForm
                label="품목그룹"
                value={WMS1060_R01Filter.itemGroup}
                optionList={itemGroupList}
                onChangeValue={(value: string) => setWMS1060_R01Filter({ itemGroup: value })}
            />
            
            {/* 3줄: 품목코드, 거래처 */}
            <SearchCodeModalForm
                label="품목코드"
                value={WMS1060_R01Filter.itemCd}
                valueLabel={WMS1060_R01Filter.itemNm}
                handleFoucsId={itemInfoHandleFocusId}
                onChange={handleItemChange}
                onDoubleClick={handleItemDoubleClick}
                onKeyDown={(e) => {
                    getFieldHandlers('itemCd').onEnterKeyDown(e);
                }}
            />
            
            <SearchCodeModalForm
                label="거래처"
                value={WMS1060_R01Filter.customCd}
                valueLabel={WMS1060_R01Filter.customNm}
                handleFoucsId={custInfoHandleFocusId}
                onChange={handleCustChange}
                onDoubleClick={handleCustDoubleClick}
                onKeyDown={(e) => {
                    getFieldHandlers('customCd').onEnterKeyDown(e);
                    if (e.key === 'Enter') {
                        onEnterKeyDown();
                    }
                }}
            />
        </SearchSectionDoubleForm>
    );
};

export default SearchSection_WMS1060_R01;
